import { Injectable } from '@angular/core';
import { Store, select } from '@ngrx/store';
import {
    FEATURE, FEATURE_ALLOW_REPLY, FEATURE_BILLING_INVOICES, FEATURE_BILLING_MANAGEPLAN_ADPRUN, FEATURE_BILLING_MANAGEPLAN_STRIPE, FEATURE_BILLING_PAYMENTDETAILS, FEATURE_BILLING_UPGRADE, FEATURE_BILLING_UPGRADE_REQUEST, FEATURE_CERTIFICATE, FEATURE_CONNECTION,
    FEATURE_CONNECTION_COLUMN_CERTIFICATE,
    FEATURE_CONNECTION_COMPLIANCE_CUSTOM,
    FEATURE_EXPORT_ADPRUN,
    FEATURE_HIRING,
    FEATURE_INTEGRATION_PAYROLL,
    FEATURE_INTEGRATION_POS,
    FEATURE_LOCATION_ADD_ADPRUN,
    FEATURE_ORDERS,
    FEATURE_PLAN,
    FEATURE_REPORTS_CONTACT_TRACING,
    FEATURE_REPORTS_LABORVSSALES,
    FEATURE_REPORTS_TIPSVSSALES,
    FEATURE_SALES,
    FEATURE_SALES_VIEW,
    FEATURE_SAVE_PREFERENCE,
    FEATURE_SCHEDULER_ADD_TIMEOFF,
    FEATURE_SCHEDULER_BUDGET,
    FEATURE_SCHEDULER_DAY_VIEW,
    FEATURE_SCHEDULER_MANAGE,
    FEATURE_TIMEOFF,
    FEATURE_WORKER_PROFILE_AVAILABILITY,
    FEATURE_WORKER_PROFILE_AVAILABILITY_ROUTE
} from './feature-id';
import { ConnectionPermission } from '@core/models/connection/connection-permission';
import { ConnectionPermissionType } from '@core/models/connection/connection-permission-type';
import { AccountRole } from '@core/models/account/account-role';
import { User } from '@core/models/user/user';
import { Company } from '@core/models/common/company';
import { PERMISSION } from './permission';
import { getCompany, getCompanyFeatures, getUser } from '@core/state/session/session-selectors';
import { take, withLatestFrom } from 'rxjs';
import { BillingSubscription } from '@core/models/billing/billing-subscription';
import { Billing } from '@core/models/billing/billing';
import { isNullOrUndefined } from '@core/utils/object';
import { BillingServiceProvider } from '@core/models/billing/billing-service-provider';
import { IAppState } from '@core/state/app/app-state';
import { BillingService } from '@core/state/billing/billing.service';

@Injectable({ providedIn: 'root' })
export class AuthPolicyService {

    constructor(
        private store$: Store<IAppState>,
        private billingService: BillingService
    ) {}

    /**
     * Validates the current session state for a feature.
     * @param {string} authId
     */
    public can(authId: string): boolean {
        let isAllowed: boolean = false;

        switch (authId) {
            case FEATURE.LOCATIONS_ALL:
            case FEATURE.SCHEDULES_ALL:
                this.store$
                    .pipe(select(getCompany), take(1))
                    .subscribe((company) => {
                        isAllowed =
                            this.hasRole(company, AccountRole.OWNER) ||
                            this.hasRole(company, AccountRole.ADMIN) ||
                            this.hasRole(company, AccountRole.SUPPORT);
                    });
                break;
            case FEATURE_ALLOW_REPLY:
                this.store$
                    .pipe(select(getCompany), take(1))
                    .subscribe((company) => {
                        isAllowed =
                            this.hasRole(company, AccountRole.OWNER) ||
                            this.hasRole(company, AccountRole.ADMIN) ||
                            this.hasRole(company, AccountRole.MANAGER) ||
                            this.hasRole(company, AccountRole.SUPPORT);
                    });
                break;

            case FEATURE_SAVE_PREFERENCE:
                isAllowed = true;
                this.store$
                    .pipe(select(getCompany), take(1))
                    .subscribe((company) => {
                        // Disable saving prefs for support roles
                        isAllowed = !this.hasRole(company, AccountRole.SUPPORT)
                    });
                break;

            default:
                isAllowed = true;
                break;
        }

        return isAllowed;
    }

    public isCompanyAuthenticated(
        company: Company,
        permissions: ConnectionPermission[],
        authId: string
    ): boolean {
        let isAllowed: boolean = false;

        if (company && authId) {
            switch (authId) {
                case FEATURE_HIRING:
                    isAllowed =
                        this.hasRole(company, AccountRole.OWNER) ||
                        this.hasRole(company, AccountRole.ADMIN) ||
                        this.hasRole(company, AccountRole.SUPPORT) ||
                        (this.hasRole(company, AccountRole.MANAGER) &&
                            this.hasUserPermission(
                                permissions,
                                PERMISSION.MANAGE_HIRING
                            ));
                    break;

                case FEATURE_ORDERS:
                    isAllowed =
                        this.hasRole(company, AccountRole.OWNER) ||
                        this.hasRole(company, AccountRole.ADMIN) ||
                        this.hasRole(company, AccountRole.SUPPORT) ||
                        (this.hasRole(company, AccountRole.MANAGER) &&
                            this.hasUserPermission(
                                permissions,
                                PERMISSION.MANAGE_DELIVERIES
                            ));
                    break;

                case FEATURE_SCHEDULER_ADD_TIMEOFF:
                case FEATURE_TIMEOFF:
                    isAllowed =
                        this.hasRole(company, AccountRole.OWNER) ||
                        this.hasRole(company, AccountRole.ADMIN) ||
                        this.hasRole(company, AccountRole.SUPPORT) ||
                        (this.hasRole(company, AccountRole.MANAGER) &&
                            this.hasUserPermission(
                                permissions,
                                PERMISSION.MANAGE_TIMEOFF
                            ));
                    break;

                case FEATURE_SCHEDULER_DAY_VIEW:
                case FEATURE_SCHEDULER_MANAGE:
                    isAllowed =
                        this.hasRole(company, AccountRole.OWNER) ||
                        this.hasRole(company, AccountRole.ADMIN) ||
                        this.hasRole(company, AccountRole.SUPPORT) ||
                        (this.hasRole(company, AccountRole.MANAGER) &&
                            this.hasUserPermission(
                                permissions,
                                PERMISSION.MANAGE_SCHEDULE
                            ));
                    break;

                // Upgrade to Premium
                case FEATURE.ADMIN_UPGRADE_PREMIUM:
                    isAllowed =
                        this.hasRole(company, AccountRole.OWNER) ||
                        this.hasRole(company, AccountRole.ADMIN) ||
                        this.hasRole(company, AccountRole.SUPPORT) ||
                        (this.hasRole(company, AccountRole.MANAGER) &&
                            this.hasUserPermission(
                                permissions,
                                PERMISSION.MANAGE_BILLING
                            ));
                    break;

                // Owner & Admin Only
                case FEATURE_INTEGRATION_POS:
                case FEATURE_INTEGRATION_PAYROLL:
                case FEATURE.COMPANY_ADMIN:
                case FEATURE.PROFILE_EDIT:
                case FEATURE.PROFILE_RECOMMENDATION_DELETE:
                case FEATURE.PROFILE_LOCATIONS_ADD:
                case FEATURE.WORKER_PROFILE_ACCESS_LEVEL:
                case FEATURE.MANAGE_WORKERS_ASSIGN_MANAGERS:
                case FEATURE.SCHEDULER_CREATE_SCHEDULE:
                case FEATURE.LOCATIONS:
                case FEATURE.LOCATIONS_DELETE:
                case FEATURE.LOCATIONS_SCHEDULES:
                    isAllowed =
                        this.hasRole(company, AccountRole.OWNER) ||
                        this.hasRole(company, AccountRole.ADMIN) ||
                        this.hasRole(company, AccountRole.SUPPORT);
                    break;

                case FEATURE.ATTENDANCE_UPGRADE:
                    isAllowed =
                        this.hasRole(company, AccountRole.OWNER) ||
                        this.hasRole(company, AccountRole.MANAGER) ||
                        this.hasRole(company, AccountRole.ADMIN) ||
                        this.hasRole(company, AccountRole.SUPPORT);
                    break;

                case FEATURE_CERTIFICATE:
                    isAllowed =
                        this.hasRole(company, AccountRole.OWNER) ||
                        this.hasRole(company, AccountRole.ADMIN) ||
                        this.hasPermission(
                            company,
                            PERMISSION.MANAGE_CERTIFICATE
                        ) ||
                        this.hasRole(company, AccountRole.SUPPORT);
                    break;

                case FEATURE.WORKER_PROFILE_EDIT:
                    isAllowed =
                        this.hasRole(company, AccountRole.OWNER) ||
                        this.hasRole(company, AccountRole.ADMIN) ||
                        this.hasRole(company, AccountRole.SUPPORT) ||
                        this.hasRole(company, AccountRole.MANAGER);
                    break;

                case FEATURE_WORKER_PROFILE_AVAILABILITY:
                    isAllowed =
                        this.hasRole(company, AccountRole.OWNER) ||
                        this.hasRole(company, AccountRole.ADMIN) ||
                        this.hasRole(company, AccountRole.SUPPORT) ||
                        this.hasUserPermission(
                            permissions,
                            PERMISSION.MANAGE_AVAILABILITY
                        );
                    break;

                case FEATURE_WORKER_PROFILE_AVAILABILITY_ROUTE:
                    /**
                     * This is just to override the permission on the base connection edit route which restricts
                     * access if manage_network is OFF. But for availability we still need to access this child route
                     */
                    isAllowed = true;
                    break;

                case FEATURE.WORKER_PROFILE_CERTIFICATIONS_EDIT:
                    this.store$
                        .pipe(select(getCompanyFeatures), take(1))
                        .subscribe((features) => {
                            isAllowed =
                                features &&
                                features.attendance &&
                                (this.hasRole(company, AccountRole.OWNER) ||
                                    this.hasRole(company, AccountRole.ADMIN) ||
                                    this.hasRole(
                                        company,
                                        AccountRole.SUPPORT
                                    ) ||
                                    (this.hasUserPermission(
                                        permissions,
                                        PERMISSION.MANAGE_CERTIFICATE
                                    ) &&
                                        this.hasUserPermission(
                                            permissions,
                                            PERMISSION.MANAGE_NETWORK
                                        )));
                        });
                    break;

                case FEATURE.CONNECTIONS_FILTER_CERTIFICATIONS:
                    this.store$
                        .pipe(select(getCompanyFeatures), take(1))
                        .subscribe((features) => {
                            isAllowed =
                                features &&
                                features.attendance &&
                                (this.hasRole(company, AccountRole.OWNER) ||
                                    this.hasRole(company, AccountRole.ADMIN) ||
                                    this.hasRole(
                                        company,
                                        AccountRole.SUPPORT
                                    ) ||
                                    this.hasUserPermission(
                                        permissions,
                                        PERMISSION.MANAGE_CERTIFICATE
                                    ));
                        });
                    break;

                // Financial Permission Only (manage_budget)
                case FEATURE.SCHEDULER_BUDGET:
                case FEATURE.POSITION_RATE:
                case FEATURE.WORKER_PROFILE_RATE:
                    isAllowed =
                        this.hasRole(company, AccountRole.OWNER) ||
                        this.hasRole(company, AccountRole.ADMIN) ||
                        this.hasPermission(company, PERMISSION.MANAGE_BUDGET) ||
                        this.hasRole(company, AccountRole.SUPPORT);
                    break;

                // Attendance budget details (manage_budget)
                case FEATURE.REPORTS:
                case FEATURE_REPORTS_LABORVSSALES:
                case FEATURE_REPORTS_TIPSVSSALES:
                case FEATURE.ATTENDANCE_BUDGET:
                case FEATURE_SCHEDULER_BUDGET:
                    isAllowed =
                        this.hasRole(company, AccountRole.OWNER) ||
                        this.hasRole(company, AccountRole.ADMIN) ||
                        this.hasPermission(company, PERMISSION.MANAGE_BUDGET) ||
                        this.hasUserPermission(
                            permissions,
                            PERMISSION.MANAGE_BUDGET
                        ) ||
                        this.hasRole(company, AccountRole.SUPPORT);
                    break;

                // Sales
                case FEATURE_SALES:
                    isAllowed =
                        this.hasRole(company, AccountRole.OWNER) ||
                        this.hasRole(company, AccountRole.ADMIN) ||
                        this.hasRole(company, AccountRole.MANAGER) ||
                        this.hasRole(company, AccountRole.SUPPORT);
                    break;
                case FEATURE_SALES_VIEW:
                    isAllowed =
                        this.hasRole(company, AccountRole.OWNER) ||
                        this.hasRole(company, AccountRole.ADMIN) ||
                        this.hasRole(company, AccountRole.SUPPORT) ||
                        this.hasPermission(company, PERMISSION.MANAGE_BUDGET) ||
                        this.hasUserPermission(
                            permissions,
                            PERMISSION.MANAGE_BUDGET
                        );
                    break;
                // Owner & Admin & Manager
                case FEATURE.PROFILE_ACCOUNT_SETTINGS:
                case FEATURE.TIMEOFF_DELETE:
                case FEATURE.UPGRADE_TRY_PREMIUM:
                    isAllowed =
                        this.hasRole(company, AccountRole.OWNER) ||
                        this.hasRole(company, AccountRole.SUPPORT) ||
                        this.hasRole(company, AccountRole.ADMIN) ||
                        this.hasRole(company, AccountRole.MANAGER);
                    break;

                case FEATURE.ATTENDANCE:
                    isAllowed =
                        this.hasUserPermission(
                            permissions,
                            PERMISSION.MANAGE_TIMECARD
                        ) ||
                        this.hasUserPermission(
                            permissions,
                            PERMISSION.MANAGE_CERTIFICATE
                        ) ||
                        this.hasRole(company, AccountRole.ADMIN) ||
                        this.hasRole(company, AccountRole.OWNER) ||
                        this.hasRole(company, AccountRole.SUPPORT);
                    break;

                case FEATURE.ATTENDANCE_ADMIN_ONLY:
                case FEATURE.ATTENDANCE_TRIAL:
                    isAllowed =
                        this.hasRole(company, AccountRole.ADMIN) ||
                        this.hasRole(company, AccountRole.OWNER) ||
                        this.hasRole(company, AccountRole.SUPPORT);
                    break;

                case FEATURE.ATTENDANCE_TIMECARDS:
                    isAllowed =
                        this.hasRole(company, AccountRole.SUPPORT) ||
                        this.hasRole(company, AccountRole.OWNER) ||
                        this.hasRole(company, AccountRole.ADMIN) ||
                        (this.hasRole(company, AccountRole.MANAGER) &&
                            this.hasUserPermission(
                                permissions,
                                PERMISSION.MANAGE_TIMECARD
                            ));
                    break;

                case FEATURE.ATTENDANCE_TIMECLOCK:
                case FEATURE_CONNECTION_COLUMN_CERTIFICATE:
                    this.billingService
                        .getSubscription()
                        .pipe(take(1))
                        .subscribe((subscription) => {
                            isAllowed = !!(
                                subscription &&
                                (subscription.isTrial ||
                                    subscription.isTrialCancelling ||
                                    subscription.isSubscribed ||
                                    subscription.isSubscribedCancelling)
                            );
                        });
                    break;

                case FEATURE.ATTENDANCE_TIMECLOCK_VIEW:
                    isAllowed =
                        this.hasRole(company, AccountRole.SUPPORT) ||
                        this.hasRole(company, AccountRole.OWNER) ||
                        this.hasRole(company, AccountRole.ADMIN) ||
                        this.hasUserPermission(
                            permissions,
                            PERMISSION.MANAGE_TIMECLOCKS
                        );
                    break;

                // Restrict payroll access to session location
                case FEATURE.ATTENDANCE_PAYROLLS:
                    this.billingService
                        .getSubscription()
                        .pipe(take(1))
                        .subscribe((subscription: BillingSubscription) => {
                            isAllowed = !!(
                                subscription &&
                                (subscription.isTrial ||
                                    subscription.isTrialCancelling ||
                                    subscription.isSubscribed ||
                                    subscription.isSubscribedCancelling)
                            );

                            // If active subscription then validate permissions
                            if (isAllowed) {
                                isAllowed =
                                    this.hasRole(
                                        company,
                                        AccountRole.SUPPORT
                                    ) ||
                                    this.hasUserPermission(
                                        (permissions || []).filter(
                                            (permission) => {
                                                return (
                                                    permission.id ===
                                                        company.id &&
                                                    permission.type ===
                                                        ConnectionPermissionType.COMPANY &&
                                                    permission.manage_timecard
                                                );
                                            }
                                        ),
                                        PERMISSION.MANAGE_TIMECARD
                                    );
                            }
                        });
                    break;

                case FEATURE.ATTENDANCE_TIMECLOCK_ADMIN:
                case FEATURE_CONNECTION_COMPLIANCE_CUSTOM:
                    this.billingService
                        .getSubscription()
                        .pipe(take(1))
                        .subscribe((subscription) => {
                            isAllowed = !!(
                                subscription &&
                                (subscription.isTrial ||
                                    subscription.isTrialCancelling ||
                                    subscription.isSubscribed ||
                                    subscription.isSubscribedCancelling)
                            );

                            // If active subscription then validate permissions
                            if (isAllowed) {
                                isAllowed =
                                    this.hasRole(company, AccountRole.ADMIN) ||
                                    this.hasRole(company, AccountRole.OWNER) ||
                                    this.hasRole(company, AccountRole.SUPPORT);
                            }
                        });
                    break;

                case FEATURE.ATTENDANCE_SETTINGS:
                    isAllowed =
                        this.hasRole(company, AccountRole.ADMIN) ||
                        this.hasRole(company, AccountRole.OWNER) ||
                        this.hasRole(company, AccountRole.SUPPORT) ||
                        (this.hasRole(company, AccountRole.MANAGER) &&
                            this.hasUserPermission(
                                permissions,
                                PERMISSION.MANAGE_ATTENDANCE_SETTINGS
                            ));
                    break;

                case FEATURE.ATTENDANCE_HOLIDAYS:
                case FEATURE.ATTENDANCE_COMPLIANCE:
                    isAllowed =
                        this.hasRole(company, AccountRole.ADMIN) ||
                        this.hasRole(company, AccountRole.OWNER) ||
                        this.hasRole(company, AccountRole.SUPPORT);
                    break;

                case FEATURE_CONNECTION:
                case FEATURE.CONNECTION_CREATE:
                case FEATURE.CONNECTION_EDIT:
                case FEATURE_REPORTS_CONTACT_TRACING:
                    isAllowed =
                        this.hasRole(company, AccountRole.SUPPORT) ||
                        this.hasRole(company, AccountRole.OWNER) ||
                        this.hasRole(company, AccountRole.ADMIN) ||
                        this.hasUserPermission(
                            permissions,
                            PERMISSION.MANAGE_NETWORK
                        );
                    break;

                case FEATURE.BILLING:
                    isAllowed =
                        this.hasRole(company, AccountRole.ADMIN) ||
                        this.hasRole(company, AccountRole.OWNER) ||
                        this.hasRole(company, AccountRole.SUPPORT);

                    if (!isAllowed) {
                        isAllowed =
                            this.hasRole(company, AccountRole.MANAGER) &&
                            this.hasUserPermission(
                                permissions,
                                PERMISSION.MANAGE_BILLING
                            );
                    }
                    break;

                case FEATURE.UPGRADE:
                case FEATURE.UPGRADE_FROM_TRIAL:
                case FEATURE.BILLING_ALERT:
                case FEATURE.BILLING_BILLINGANDPAYMENTS:
                    isAllowed =
                        this.hasRole(company, AccountRole.ADMIN) ||
                        this.hasRole(company, AccountRole.OWNER) ||
                        this.hasRole(company, AccountRole.SUPPORT);

                    if (!isAllowed) {
                        isAllowed =
                            this.hasRole(company, AccountRole.MANAGER) &&
                            this.hasUserPermission(
                                permissions,
                                PERMISSION.MANAGE_BILLING
                            );
                    }

                    break;

                case FEATURE_PLAN:
                    isAllowed =
                        this.hasRole(company, AccountRole.ADMIN) ||
                        this.hasRole(company, AccountRole.OWNER) ||
                        this.hasRole(company, AccountRole.SUPPORT);

                    if (
                        !isAllowed &&
                        this.hasRole(company, AccountRole.MANAGER)
                    ) {
                        this.billingService
                            .getSubscription()
                            .pipe(
                                withLatestFrom(
                                    this.billingService.getBilling()
                                ),
                                take(1)
                            )
                            .subscribe(
                                ([subscription, billing]: [
                                    BillingSubscription,
                                    Billing
                                ]) => {
                                    // the manager can access plan if
                                    // never trialed OR trial expired
                                    isAllowed =
                                        !subscription ||
                                        (subscription.isCancelled &&
                                            billing &&
                                            !billing.last_payment_at);
                                }
                            );
                    }
                    break;

                case FEATURE.TRIAL_COUNTDOWN:
                    isAllowed =
                        this.hasRole(company, AccountRole.ADMIN) ||
                        this.hasRole(company, AccountRole.OWNER) ||
                        this.hasRole(company, AccountRole.SUPPORT);
                    break;

                case FEATURE.SETUP_GUIDE_SCHEDULING_ESSENTIALS:
                    isAllowed =
                        company &&
                        this.canManageOneOrMoreSchedules(permissions);
                    break;

                case FEATURE.SETUP_GUIDE_SCHEDULING_ESSENTIALS_ASSIGN_MANAGER:
                    isAllowed =
                        company &&
                        this.canManageOneOrMoreSchedules(permissions) &&
                        (this.hasRole(company, AccountRole.ADMIN) ||
                            this.hasRole(company, AccountRole.OWNER));
                    break;

                case FEATURE.SETUP_GUIDE_ATTENDANCE_TIME_CLOCK_ESSENTIALS:
                case FEATURE.SETUP_GUIDE_ATTENDANCE_COMPLIANCE:
                    isAllowed =
                        company &&
                        (this.hasRole(company, AccountRole.ADMIN) ||
                            this.hasRole(company, AccountRole.OWNER));
                    break;

                case FEATURE.SETUP_GUIDE_SET_COMPANY_ACCOUNT:
                    isAllowed =
                        company &&
                        (this.hasRole(company, AccountRole.ADMIN) ||
                            this.hasRole(company, AccountRole.OWNER) ||
                            this.hasRole(company, AccountRole.MANAGER));
                    break;

                case FEATURE.SETUP_GUIDE_SET_COMPANY_ACCOUNT_NEW_LOCATION:
                    isAllowed =
                        company &&
                        (this.hasRole(company, AccountRole.ADMIN) ||
                            this.hasRole(company, AccountRole.OWNER) ||
                            (this.hasRole(company, AccountRole.MANAGER) &&
                                this.canManageOneOrMoreSchedules(permissions) &&
                                this.isCompanyAuthenticated(
                                    company,
                                    permissions,
                                    FEATURE.LOCATIONS
                                )));
                    break;

                case FEATURE.SETUP_GUIDE_SCHEDULING_ESSENTIALS_CONFIGURE_SCHEDULE:
                case FEATURE.SETUP_GUIDE_SET_COMPANY_ACCOUNT_NEW_SCHEDULE:
                    isAllowed =
                        company &&
                        (this.hasRole(company, AccountRole.ADMIN) ||
                            this.hasRole(company, AccountRole.OWNER) ||
                            (this.hasRole(company, AccountRole.MANAGER) &&
                                this.canManageOneOrMoreSchedules(permissions)));
                    break;

                case FEATURE.SETUP_GUIDE_ONBOARD_WORKERS:
                    isAllowed =
                        company &&
                        (this.hasRole(company, AccountRole.ADMIN) ||
                            this.hasRole(company, AccountRole.OWNER) ||
                            this.isCompanyAuthenticated(
                                company,
                                permissions,
                                FEATURE_CONNECTION
                            ));
                    break;

                case FEATURE.SETUP_GUIDE_POWER_FEATURES:
                    isAllowed =
                        company &&
                        this.canManageOneOrMoreSchedules(permissions);
                    break;

                case FEATURE.SETUP_GUIDE_POWER_FEATURES_SET_BUDGET:
                    isAllowed =
                        this.isCompanyAuthenticated(
                            company,
                            permissions,
                            FEATURE.SETUP_GUIDE_POWER_FEATURES
                        ) &&
                        this.isCompanyAuthenticated(
                            company,
                            permissions,
                            FEATURE.SCHEDULER_BUDGET
                        ) &&
                        this.canManageOneOrMoreSchedules(permissions);
                    break;

                case FEATURE.SETUP_GUIDE_ATTENDANCE_MANAGEMENT_APPROVE_TIMECARD:
                case FEATURE.SETUP_GUIDE_ATTENDANCE_MANAGEMENT_BULK_APPROVE:
                case FEATURE.SETUP_GUIDE_ATTENDANCE_MANAGEMENT_CREATE_TIMECARD:
                case FEATURE.SETUP_GUIDE_ATTENDANCE_MANAGEMENT_EDIT_TIMECARD:
                    isAllowed =
                        company &&
                        this.canManageOneOrMoreSchedules(permissions) &&
                        (this.hasRole(company, AccountRole.ADMIN) ||
                            (this.hasRole(company, AccountRole.MANAGER) &&
                                this.hasUserPermission(
                                    permissions,
                                    PERMISSION.MANAGE_TIMECARD
                                )) ||
                            this.hasRole(company, AccountRole.OWNER));
                    break;

                case FEATURE.SETUP_GUIDE_ATTENDANCE_COMPLIANCE_VIEW_CERTIFICATIONS:
                    isAllowed =
                        company &&
                        (this.hasRole(company, AccountRole.ADMIN) ||
                            (this.hasRole(company, AccountRole.MANAGER) &&
                                this.hasUserPermission(
                                    permissions,
                                    PERMISSION.MANAGE_CERTIFICATE
                                )) ||
                            this.hasRole(company, AccountRole.OWNER));
                    break;

                case FEATURE.SETUP_GUIDE_ATTENDANCE_COMPLIANCE_VIEW_WARNINGS:
                    isAllowed =
                        company &&
                        this.canManageOneOrMoreSchedules(permissions) &&
                        (this.hasRole(company, AccountRole.ADMIN) ||
                            this.hasRole(company, AccountRole.MANAGER) ||
                            this.hasRole(company, AccountRole.OWNER));
                    break;

                case FEATURE.SETUP_GUIDE_ATTENDANCE_MANAGEMENT_SEND_FOR_APPROVAL:
                case FEATURE.SETUP_GUIDE_ATTENDANCE_MANAGEMENT_PAYROLL_CLOSE_EXPORT:
                case FEATURE.SETUP_GUIDE_ATTENDANCE_MANAGEMENT_PAYROLL_HISTORY:
                    isAllowed =
                        company &&
                        this.canManageOneOrMoreLocationsPayroll(permissions) &&
                        (this.hasRole(company, AccountRole.ADMIN) ||
                            this.hasRole(company, AccountRole.MANAGER) ||
                            this.hasRole(company, AccountRole.OWNER));
                    break;

                case FEATURE.SUPPORT:
                    this.store$
                        .pipe(select(getUser), take(1))
                        .subscribe((user) => {
                            isAllowed = this.hasUserGroup(user, 'supports');
                        });
                    break;

                case FEATURE_BILLING_UPGRADE:
                    isAllowed =
                        this.hasRole(company, AccountRole.ADMIN) ||
                        this.hasRole(company, AccountRole.OWNER) ||
                        (this.hasRole(company, AccountRole.MANAGER) &&
                            this.hasUserPermission(
                                permissions,
                                PERMISSION.MANAGE_BILLING
                            ));
                    break;

                case FEATURE_BILLING_UPGRADE_REQUEST:
                    isAllowed =
                        this.hasRole(company, AccountRole.MANAGER) &&
                        !this.hasUserPermission(
                            permissions,
                            PERMISSION.MANAGE_BILLING
                        );
                    break;

                case FEATURE_BILLING_MANAGEPLAN_STRIPE:
                case FEATURE_BILLING_INVOICES:
                case FEATURE_BILLING_PAYMENTDETAILS:
                    this.billingService
                        .getBilling()
                        .pipe(take(1))
                        .subscribe((billing: Billing) => {
                            if (
                                billing &&
                                isNullOrUndefined(billing.service_provider)
                            ) {
                                isAllowed = true;
                            } else {
                                isAllowed =
                                    billing?.service_provider ===
                                    BillingServiceProvider.STRIPE;
                            }
                        });
                    break;

                case FEATURE_LOCATION_ADD_ADPRUN:
                case FEATURE_BILLING_MANAGEPLAN_ADPRUN:
                case FEATURE_EXPORT_ADPRUN:
                    this.billingService
                        .getBilling()
                        .pipe(take(1))
                        .subscribe((billing: Billing) => {
                            isAllowed =
                                billing?.service_provider ===
                                BillingServiceProvider.ADPRUN;
                        });
                    break;

                case FEATURE.LOGBOOKS:
                    isAllowed =
                        (company &&
                            this.hasRole(company, AccountRole.ADMIN)) ||
                        (company &&
                            this.hasRole(company, AccountRole.MANAGER) &&
                            this.canManageOneOrMoreSchedules(permissions));
                    break;

                case FEATURE.LOGBOOKS_POST_DELETE:
                    isAllowed =
                        company &&
                        this.hasRole(company, AccountRole.ADMIN) &&
                        this.hasUserPermission(
                            permissions,
                            PERMISSION.MANAGE_SCHEDULE
                        );
                    break;

                default:
                    isAllowed = true;
                    break;
            }
        }

        return isAllowed;
    }

    public canManageOneOrMoreSchedules(
        permissions: ConnectionPermission[]
    ): boolean {
        return (
            permissions &&
            permissions.some(
                (connectionPermission) => connectionPermission.canManageSchedule
            )
        );
    }

    public canManageOneOrMoreLocationsPayroll(
        permissions: ConnectionPermission[]
    ): boolean {
        return (
            permissions &&
            permissions.some(
                (connectionPermission) =>
                    connectionPermission.type ===
                        ConnectionPermissionType.LOCATION &&
                    connectionPermission.manage_timecard
            )
        );
    }

    /**
     * Validates company permission.
     * @param company
     * @param permission
     */
    private hasPermission(company, permission): boolean {
        let hasPermission = false;
        if (
            company &&
            company.permissions && Object.hasOwn(company.permissions, permission)
        ) {
            hasPermission = company.permissions[permission];
        }

        return hasPermission || this.hasRole(company, AccountRole.SUPPORT);
    }

    /**
     * Validates company role.
     * @param company
     * @param role
     */
    private hasRole(company, role): boolean {
        return company && company.role && company.role === role;
    }

    private hasUserPermission(
        permissions: ConnectionPermission[],
        permission: string
    ): boolean {
        return (
            permissions &&
            permissions.some(
                (connectionPermission) => connectionPermission[permission]
            )
        );
    }

    /**
     * Validate users group access.
     * @param {User} user
     * @param {string} group
     * @returns {boolean}
     */
    private hasUserGroup(user: User, group: string): boolean {
        return user && user.groups && user.groups.includes(group);
    }
}